import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  EDateSortParams,
  ESortDirection,
  TSortParams,
} from 'data/types/generic.types';
import { dateShortenFormat } from 'helpers/utils';

dayjs.extend(customParseFormat);

export type TSessionDateLike<P extends string = string> = {
  partType: P;
  date: string | null;
};

export type TNextSessionNote = 'NEXT' | 'ALL_PAST';

// Color treatment of one session date while the table is sorted by date
// (GROW-4300): 'next' (red) for the backend-flagged next session and for a
// session happening today, 'past' (blue) for anything that already happened,
// null for other future sessions.
export type TSessionDateTone = 'past' | 'next';

// Default order of the Workshops and Events tables (GROW-4270): next session
// date ascending, which is also what the backend applies when no sort param is
// sent. Sent explicitly so the column header shows it and Reset Filters or an
// un-sorted column return to it.
export const DEFAULT_DATE_SORT_COLUMN: TSortParams = {
  sortField: EDateSortParams.NEXT_SESSION_DATE,
  sortDirection: ESortDirection.ascend,
};

// The Session/Event Date column sorts on the backend's PART_DATE field. An
// empty sortColumn (e.g. one persisted before the default existed) means the
// backend default, which is the same order.
export const isSortedByNextSessionDate = ({ sortField }: TSortParams) =>
  !sortField || sortField === EDateSortParams.NEXT_SESSION_DATE;

// Caption for a row's Session/Event Date cell while the table is sorted by
// date, based on the backend's nextSessionPart flag (GROW-4274): the flagged
// part is the row's next session; a null flag with dated sessions means every
// session is already past.
export const getNextSessionNote = (
  sessionDates: TSessionDateLike[],
  nextSessionPart: string | null | undefined,
): TNextSessionNote | null => {
  if (nextSessionPart) {
    return 'NEXT';
  }
  return sessionDates.some(({ date }) => date !== null) ? 'ALL_PAST' : null;
};

// Session dates reach the UI already formatted (dateShortenFormat); mocks use
// ISO dates. `today` is injectable for tests.
export const getSessionDateTone = (
  { date, partType }: TSessionDateLike,
  nextSessionPart: string | null | undefined,
  today = dayjs(),
): TSessionDateTone | null => {
  if (partType === nextSessionPart) {
    return 'next';
  }
  if (!date) {
    return null;
  }
  const parsed = dayjs(date, [dateShortenFormat, 'YYYY-MM-DD'], true);
  if (!parsed.isValid()) {
    return null;
  }
  if (parsed.isSame(today, 'day')) {
    return 'next';
  }
  return parsed.isBefore(today, 'day') ? 'past' : null;
};
